import type { GraphQLResult } from 'aws-amplify/api'
import { ReviewStatus } from '@skop-app/lib-types'

import { getAmplifyClient } from '@/services/amplifyClient'

import {
  probeProjectMembershipQuery,
  probeProjectsQuery,
  probeReferencePropertiesByOrganizationQuery,
  probeReferencePropertiesByUserQuery,
  probeSavedSearchByOrganizationQuery,
  probeSavedSearchByUserQuery,
} from './manageActivity.graphql'
import { resolveManageActivityProbeResults } from './manageActivity.probe.shared'

const client = getAmplifyClient()
const EXISTENCE_LIMIT = 25
const MAX_PROBE_PAGES = 5
const savedSearchStatusFilter = { status: { ne: 'deleted' } }

type ProbeManageActivityParams = {
  userId: string
  organizationId: string | undefined
}

type ActivityItem = {
  id?: string
  project?: { id?: string } | null
}

type ActivityPage = {
  items?: Array<ActivityItem | null> | null
  nextToken?: string | null
}

const hasMatchingActivity = async (
  query: string,
  variables: Record<string, unknown>,
  connectionName: string,
  matches: (item: ActivityItem) => boolean = (item) => item.id != null,
): Promise<boolean> => {
  let nextToken: string | undefined
  const seenTokens = new Set<string>()
  let pageCount = 0

  do {
    if (pageCount >= MAX_PROBE_PAGES) {
      throw new Error('Manage activity probe exceeded its page budget')
    }
    pageCount += 1
    const result = (await client.graphql({
      query,
      variables: { ...variables, limit: EXISTENCE_LIMIT, nextToken },
    })) as GraphQLResult<Record<string, ActivityPage | null | undefined>>
    const page = result.data?.[connectionName]

    if (result.errors?.length || !page) {
      throw new Error('Unable to determine whether manage activity exists')
    }
    if (page.items?.some((item) => item != null && matches(item))) {
      return true
    }

    nextToken = page.nextToken ?? undefined
    if (nextToken && seenTokens.has(nextToken)) {
      throw new Error(
        'Detected repeated nextToken while probing manage activity',
      )
    }
    if (nextToken) seenTokens.add(nextToken)
  } while (nextToken)

  return false
}

const hasProjectViaOrganization = (organizationId: string): Promise<boolean> =>
  hasMatchingActivity(
    probeProjectsQuery,
    { organizationID: organizationId },
    'projectsByOrganization',
  )

const hasProjectViaMembership = (userId: string): Promise<boolean> =>
  hasMatchingActivity(
    probeProjectMembershipQuery,
    { userID: userId, filter: { type: { eq: 'PROJECT' } } },
    'organizationMemberbyUser',
    (item) => item.project?.id != null,
  )

const hasReferenceProperty = async ({
  userId,
  organizationId,
}: ProbeManageActivityParams): Promise<boolean> => {
  const checks = [
    hasMatchingActivity(
      probeReferencePropertiesByUserQuery,
      {
        byUserID: userId,
        filter: { organizationID: { attributeExists: false } },
      },
      'listReferencePropertyByByUserID',
    ),
  ]
  if (organizationId) {
    checks.push(
      hasMatchingActivity(
        probeReferencePropertiesByOrganizationQuery,
        {
          organizationID: organizationId,
          filter: { status: { ne: ReviewStatus.REJECTED } },
        },
        'referencesByOrganization',
      ),
    )
  }
  return resolveManageActivityProbeResults(await Promise.allSettled(checks))
}

const hasSavedSearchAlert = async ({
  userId,
  organizationId,
}: ProbeManageActivityParams): Promise<boolean> => {
  if (
    organizationId &&
    (await hasMatchingActivity(
      probeSavedSearchByOrganizationQuery,
      { organizationID: organizationId, filter: savedSearchStatusFilter },
      'savedSearchByOrganization',
    ))
  ) {
    return true
  }

  return hasMatchingActivity(
    probeSavedSearchByUserQuery,
    { byUserID: userId, filter: savedSearchStatusFilter },
    'savedSearchByUser',
  )
}

/**
 * Returns true when the user has at least one accessible project, reference, or alert.
 */
export async function probeManageActivity(
  params: ProbeManageActivityParams,
): Promise<boolean> {
  const checks = [
    ...(params.organizationId
      ? [hasProjectViaOrganization(params.organizationId)]
      : []),
    hasProjectViaMembership(params.userId),
    hasReferenceProperty(params),
    hasSavedSearchAlert(params),
  ]

  const results = await Promise.allSettled(checks)
  return resolveManageActivityProbeResults(results)
}
